import type { CatalogProduct } from './types'

const STORAGE_KEY = 'shop_recently_viewed_products_v1'
const MAX_ENTRIES = 20

export type RecentlyViewedProduct = {
  id: string
  name: string
  slug: string
  publicCode?: string
  imageUrl?: string
  price?: number
  currency: string
  viewedAt: string
}

const toPrice = (product: CatalogProduct) => {
  const candidate = typeof product.price === 'number' && product.price
  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : undefined
}

const toImageUrl = (product: CatalogProduct) => {
  const direct = product.mediaVariants?.large || product.mediaVariants?.medium || product.mediaVariants?.small
  if (direct && direct.trim()) return direct.trim()
  const gallery = Array.isArray(product.mediaGallery) ? product.mediaGallery : []
  const firstGalleryImage = gallery.find((entry) => typeof entry === 'string' && entry.trim().length > 0)
  return firstGalleryImage?.trim() || undefined
}

const normalizeEntry = (entry: unknown): RecentlyViewedProduct | null => {
  if (!entry || typeof entry !== 'object') return null
  const raw = entry as Record<string, unknown>
  const id = typeof raw.id === 'string' ? raw.id.trim() : ''
  const name = typeof raw.name === 'string' ? raw.name.trim() : ''
  const slug = typeof raw.slug === 'string' ? raw.slug.trim() : ''
  const viewedAt = typeof raw.viewedAt === 'string' ? raw.viewedAt.trim() : ''
  if (!id || !name || !slug || !viewedAt) return null

  return {
    id,
    name,
    slug,
    publicCode: typeof raw.publicCode === 'string' && raw.publicCode.trim() ? raw.publicCode.trim() : undefined,
    imageUrl: typeof raw.imageUrl === 'string' && raw.imageUrl.trim() ? raw.imageUrl.trim() : undefined,
    price: typeof raw.price === 'number' && Number.isFinite(raw.price) ? raw.price : undefined,
    currency: typeof raw.currency === 'string' && raw.currency.trim() ? raw.currency.trim() : 'EUR',
    viewedAt,
  }
}

const loadRecentlyViewed = (): RecentlyViewedProduct[] => {
  if (typeof window === 'undefined') return []
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    if (!value) return []
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((entry) => normalizeEntry(entry))
      .filter((entry): entry is RecentlyViewedProduct => entry !== null)
      .slice(0, MAX_ENTRIES)
  } catch {
    return []
  }
}

export const readRecentlyViewedProducts = (): RecentlyViewedProduct[] => loadRecentlyViewed()

export const trackRecentlyViewedProduct = (product: CatalogProduct | null) => {
  if (!product || typeof window === 'undefined') return
  const id = product._id?.trim()
  const slug = product.slug?.trim()
  const name = product.name?.trim()
  if (!id || !slug || !name) return

  const entry: RecentlyViewedProduct = {
    id,
    slug,
    name,
    publicCode: product.publicCode?.trim() || undefined,
    imageUrl: toImageUrl(product),
    price: toPrice(product),
    currency: 'EUR',
    viewedAt: new Date().toISOString(),
  }

  try {
    const current = loadRecentlyViewed()
    const deduplicated = current.filter((candidate) => candidate.id !== entry.id)
    const next = [entry, ...deduplicated].slice(0, MAX_ENTRIES)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Silent fallback: recently viewed is non-critical UX data.
  }
}
